import { z } from 'zod';
/**
 * Exposé v2 (E1): the structured Verkaufsdokumentation model.
 *
 * Split per the architecture doc (EXPOSE-GENERATOR-RESEARCH-VOORBEELD.md):
 *  - `ExposeEckdaten`  = deterministic FACTS derived 1:1 from the checklist
 *    (labels resolved). The PDF, the CRM preview and the publication all read
 *    the SAME object, so numbers can never diverge between surfaces.
 *  - `ExposeSectionTexts` = the PROSE per section (template now, LLM in E6) —
 *    editable by staff without touching the facts, and regenerable without
 *    losing the staff's block toggles / global override.
 *  - `EXPOSE_BLOCKS`   = the brochure's building blocks (example exposé's 21
 *    topics folded into stable keys). Later phases add renderers for keys that
 *    E1 does not render yet — the keys are fixed HERE so `blocksJsonb` stays
 *    forward-compatible.
 *
 * PII rule (hard): NO owner data (eigentuemer*), NO internal prices
 * (Mindestverkaufspreis, hedonischer Schätzwert, Wunschpreis as such) ever
 * enter this schema — the exposé is a public sales document.
 */
// ─────────────────────────── German label maps ──────────────────────────────
// Single source for backend FACTS mapping, the brochure PDF and the CRM
// preview. Keys mirror the checklist enums in ./pipeline.
export const IMMOBILIEN_TYP_LABELS = {
    wohnung: 'Wohnung',
    efh: 'Einfamilienhaus',
    mfh: 'Mehrfamilienhaus',
    gewerbe: 'Gewerbeobjekt',
    grundstueck: 'Grundstück',
};
/** E19: Objektkategorie (steers the Mieterspiegel block + rendite prose). */
export const OBJEKTKATEGORIE_LABELS = {
    selbstbewohnt: 'Selbstbewohntes Wohneigentum',
    renditeobjekt: 'Renditeobjekt',
};
export const NUTZUNG_LABELS = {
    wohnen: 'Wohnen',
    gewerbe: 'Gewerbe',
    gemischt: 'Gemischte Nutzung',
};
export const TRANSAKTIONSTYP_LABELS = {
    verkauf: 'Verkauf',
    miete: 'Miete',
};
export const VERKAUFSVERFAHREN_LABELS = {
    festpreis: 'Festpreis',
    bieterverfahren: 'Bieterverfahren',
    auf_anfrage: 'Auf Anfrage',
};
export const VERFUEGBARKEIT_LABELS = {
    sofort: 'Per sofort',
    nach_vereinbarung: 'Nach Vereinbarung',
    per_datum: 'Per Datum',
};
export const ZUSTAND_LABELS = {
    neuwertig: 'Neuwertig',
    renoviert: 'Renoviert',
    gepflegt: 'Gepflegt',
    renovationsbeduerftig: 'Renovationsbedürftig',
};
export const EIGENTUMSFORM_EXPOSE_LABELS = {
    alleineigentum: 'Alleineigentum',
    miteigentum: 'Miteigentum',
    stockwerkeigentum: 'Stockwerkeigentum',
    baurecht: 'Baurecht',
};
export const WAERMEERZEUGUNG_LABELS = {
    waermepumpe: 'Wärmepumpe',
    gas: 'Gasheizung',
    oel: 'Ölheizung',
    fernwaerme: 'Fernwärme',
    pellets: 'Pelletsheizung',
    elektro: 'Elektroheizung',
    holz: 'Holzheizung',
    solar: 'Solarthermie',
    andere: 'Andere',
};
export const WAERMEVERTEILUNG_LABELS = {
    bodenheizung: 'Bodenheizung',
    radiatoren: 'Radiatoren',
    deckenheizung: 'Deckenheizung',
    andere: 'Andere',
};
export const NIEDRIGENERGIE_LABELS = {
    nein: '',
    minergie: 'Minergie',
    minergie_p: 'Minergie-P',
    minergie_a: 'Minergie-A',
    andere: 'Niedrigenergie',
};
export const MWST_LABELS = {
    nicht_optiert: 'Nicht MWST-optiert',
    optiert: 'MWST-optiert',
};
export const BAUTEIL_LABELS = {
    kueche: 'Küche',
    bad: 'Bad',
    boden: 'Böden',
    fenster: 'Fenster',
    mauerwerk: 'Mauerwerk',
};
export const BAUTEIL_QUALITAET_LABELS = {
    einfach: 'Einfach',
    mittel: 'Mittel',
    gehoben: 'Gehoben',
    luxurioes: 'Luxuriös',
};
export const BAUTEIL_ZUSTAND_LABELS = {
    neuwertig: 'Neuwertig',
    gut: 'Gut',
    mittel: 'Mittel',
    renovationsbeduerftig: 'Renovationsbedürftig',
};
/** The 51 Ausstattung chips — proper de-CH labels (no key humanising). */
export const AUSSTATTUNG_LABELS = {
    abstellkammer: 'Abstellkammer',
    auslaenderkontingent: 'Ausländerkontingent',
    balkon: 'Balkon',
    bauland_erschlossen: 'Bauland erschlossen',
    bergsicht: 'Bergsicht',
    buero_homeoffice: 'Büro / Homeoffice',
    carport: 'Carport',
    cheminee: 'Cheminée',
    doppelgarage: 'Doppelgarage',
    eckhaus: 'Eckhaus',
    erdgeschoss: 'Erdgeschoss',
    erstwohnsitz: 'Erstwohnsitz',
    garage: 'Garage',
    garten: 'Garten',
    gasanschluss: 'Gasanschluss',
    geschirrspueler: 'Geschirrspüler',
    grillplatz: 'Grillplatz',
    hanglage: 'Hanglage',
    haustiere_erlaubt: 'Haustiere erlaubt',
    hochparterre: 'Hochparterre',
    im_baurecht: 'Im Baurecht',
    wohngemeinschaft: 'Wohngemeinschaft',
    kabelfernsehen: 'Kabelfernsehen',
    kachelofen: 'Kachelofen',
    kamin: 'Kamin',
    keller: 'Keller',
    kinderfreundlich: 'Kinderfreundlich',
    ladestation_elektroauto: 'Ladestation Elektroauto',
    lift: 'Lift',
    mietkautionsgarantie: 'Mietkautionsgarantie',
    minergie: 'Minergie',
    multimediale_verkabelung: 'Multimediale Verkabelung',
    nichtraucher: 'Nichtraucher',
    parkplatz: 'Parkplatz',
    projektiert: 'Projektiert',
    rollstuhlgaengig: 'Rollstuhlgängig',
    ruhig: 'Ruhig',
    sauna: 'Sauna',
    seesicht: 'Seesicht',
    sommerlaube: 'Sommerlaube',
    sonnig: 'Sonnig',
    spielraum: 'Spielraum',
    suedhang: 'Südhang',
    swimmingpool: 'Swimmingpool',
    terrasse: 'Terrasse',
    tumbler: 'Tumbler',
    virtuelle_besichtigung: 'Virtuelle Besichtigung',
    waschmaschine: 'Waschmaschine',
    waeschetrockner: 'Wäschetrockner',
    weinkeller: 'Weinkeller',
    zweitwohnsitz: 'Zweitwohnsitz',
    zwischennutzung: 'Zwischennutzung',
};
export const ANNEHMLICHKEITEN_LABELS = {
    ruhige_lage: 'Ruhige Lage',
    aussicht: 'Aussicht',
    bergsicht: 'Bergsicht',
    seesicht: 'Seesicht',
    haustiere_erlaubt: 'Haustiere erlaubt',
    kinderfreundlich: 'Kinderfreundlich',
    seenaehe: 'Seenähe',
    zentrale_lage: 'Zentrale Lage',
    oev_naehe: 'ÖV-Nähe',
    privatsphaere: 'Privatsphäre',
    sonnige_ausrichtung: 'Sonnige Ausrichtung',
    offenes_wohnkonzept: 'Offenes Wohnkonzept',
    gehobener_ausbaustandard: 'Gehobener Ausbaustandard',
};
export const ANSCHLUESSE_LABELS = {
    glasfaser: 'Glasfaser',
    dsl: 'DSL',
    kabel: 'Kabelanschluss',
    fuenf_g: '5G',
};
// ───────────────────────────── GEAK (checklist add-on) ──────────────────────
export const GEAK_KLASSEN = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
export const geakKlasseSchema = z.enum(GEAK_KLASSEN);
// ───────────────────────────── Eckdaten (FACTS) ─────────────────────────────
const optStr = z.string().optional();
const optNum = z.number().optional();
export const exposeAddressSchema = z
    .object({ strasse: z.string(), plz: z.string(), ort: z.string(), kanton: z.string() })
    .partial();
/**
 * Deterministically derived facts. Every label is resolved at mapping time so
 * consumers never need the enum tables. All fields optional — the checklist is
 * lenient/partial by design. `quelle` on the price documents which checklist
 * field won (inserat vs. wunsch), for staff transparency in the CRM.
 */
export const exposeEckdatenSchema = z
    .object({
    titel: z.string(),
    adresse: exposeAddressSchema,
    adresseText: z.string(),
    gemeinde: z.string(),
    gemeindeBfs: z.number().int(),
    kanton: z.string(),
    objektTyp: optStr,
    objektTypLabel: optStr,
    nutzungLabel: optStr,
    /** E19: "Selbstbewohntes Wohneigentum" | "Renditeobjekt". */
    objektkategorie: optStr,
    objektkategorieLabel: optStr,
    transaktionstypLabel: optStr,
    verkaufsverfahrenLabel: optStr,
    verfuegbarkeitLabel: optStr,
    eigentumsformLabel: optStr,
    neubauUmbau: z.boolean(),
    zimmer: optNum,
    badezimmer: optNum,
    separateWc: optNum,
    nasszellenTotal: optNum,
    etagen: optNum,
    etage: optStr,
    raumhoehe: optNum,
    wohneinheiten: optNum,
    zustandLabel: optStr,
    baujahr: optNum,
    letztesRenovationsjahr: optNum,
    flaechen: z
        .object({
        nettowohnflaeche: optNum,
        nutzflaeche: optNum,
        grundstuecksflaeche: optNum,
        balkonflaeche: optNum,
        terrasseGarten: optNum,
        gebaeudevolumenGva: optNum,
        garagenparkplaetze: optNum,
        aussenparkplaetze: optNum,
    })
        .partial(),
    preis: z
        .object({
        verkaufspreis: optNum,
        /** Which checklist field the public price came from. */
        quelle: z.enum(['verkaufspreisInserat', 'wunschverkaufspreis']).optional(),
        preisProM2: optNum,
        nebenkostenJahr: optNum,
        eigenmietwert: optNum,
        mwstLabel: optStr,
    })
        .partial(),
    energie: z
        .object({
        waermeerzeugungLabel: optStr,
        waermeverteilungLabel: optStr,
        heizkostenJahr: optNum,
        energiestandardLabel: optStr,
        geak: z
            .object({
            klasse: geakKlasseSchema,
            gebaeudehuelle: geakKlasseSchema.optional(),
            gesamtenergie: geakKlasseSchema.optional(),
            co2: geakKlasseSchema.optional(),
            kwhProM2: optNum,
            co2ProM2: optNum,
        })
            .optional(),
    })
        .partial(),
    ausstattung: z.array(z.string()),
    ausstattungLabels: z.array(z.string()),
    annehmlichkeitenLabels: z.array(z.string()),
    anschluesseLabels: z.array(z.string()),
    bastelraum: z.boolean().optional(),
    bauteile: z
        .array(z.object({
        key: z.string(),
        label: z.string(),
        qualitaetLabel: optStr,
        zustandLabel: optStr,
    }))
        .optional(),
})
    .partial();
// ───────────────────────────── Prose sections ───────────────────────────────
/** Staff-editable prose keys (regenerate overwrites these — the CRM warns). */
export const EXPOSE_TEXT_SECTIONS = [
    'teaser',
    'quote',
    'beschreibung',
    'lage',
    'highlightsText',
    'ausstattungText',
    'gemeindeText',
];
export const exposeTextSectionSchema = z.enum(EXPOSE_TEXT_SECTIONS);
export const exposeSectionTextsSchema = z
    .object({
    /** Cover teaser (2–3 sentences). */
    teaser: z.string(),
    /** Tagline, rendered as «quote» above the Objektbeschreibung. */
    quote: z.string(),
    beschreibung: z.string(),
    lage: z.string(),
    highlightsText: z.string(),
    ausstattungText: z.string(),
    gemeindeText: z.string(),
    /** LEGACY (E17: rooms page retired, no longer generated or rendered) —
     *  field kept so E16-era stored sectionsJsonb keeps parsing. */
    raeume: z.array(z.object({ key: z.string(), titel: z.string(), text: z.string() })),
    /** "Investitionen & Highlights" bullets. */
    investitionen: z.array(z.string()),
})
    .partial();
// ───────────────────────────── Brochure blocks ──────────────────────────────
/**
 * Stable block keys for the whole target brochure (research doc §1). E1
 * renders the subset marked below; later phases add renderers WITHOUT new
 * keys, so staff toggles persisted today stay valid. A block also auto-hides
 * when its data is missing — a toggle can only switch OFF an available block.
 */
export const EXPOSE_BLOCKS = [
    'cover', //            E1
    'inhalt', //           E1 (TOC)
    'uebersicht', //       E1 (Objektübersicht tiles; +map in E4)
    'beschreibung', //     E1 (quote + prose + highlights/investitionen)
    'raeume', //           LEGACY (E17: rooms page retired; key kept so stored blocksJsonb keeps parsing)
    'detail', //           E1 (Detailbeschrieb table + Eigenschaften)
    'energie', //          E1 (Heizung; GEAK ladder when data present)
    'grundriss', //        E2
    'galerie', //          E2
    'infrastruktur', //    E3 (POI lists; maps in E4)
    'umgebung', //         LEGACY (E15: merged into 'gemeinde'; key kept so stored blocksJsonb keeps parsing)
    'standort', //         E3 (Standortübersicht + detail pages)
    'gemeinde', //         E5
    'mieterspiegel', //    E19 (Rendite: anonymised rent-roll table, CRM-entered rows)
    'preisInvestition', // E5
    'steuern', //          E5
    'demografie', //       E5
    'ueberUns', //         E1 (static brand block)
    'kaufprozess', //      E1 (static brand block)
    'disclaimer', //       E1 (always on — cannot be toggled off)
];
export const exposeBlockSchema = z.enum(EXPOSE_BLOCKS);
/** Blocks staff may not disable (legal / structural). `inhalt` is structural:
 *  the TOC page is always rendered, so the toggle would be a silent no-op. */
export const EXPOSE_BLOCKS_LOCKED = ['cover', 'inhalt', 'disclaimer'];
/** Partial map; absent key = enabled (default on). partialRecord because v4's
 *  z.record() with an enum key requires every key to be present. */
export const exposeBlocksSchema = z.partialRecord(exposeBlockSchema, z.boolean());
export const isExposeBlockEnabled = (blocks, key) => EXPOSE_BLOCKS_LOCKED.includes(key) ? true : (blocks?.[key] ?? true);
// ───────────────────── Standort-Intelligence (E3) ───────────────────────────
// Snapshot of the free federal open-data lookups (geo.admin.ch identify/GFI,
// swisstopo geocoding, OSM Overpass POIs) taken at generate time — research
// doc §2a/§2d. Everything optional: each lookup degrades independently
// (`partial` flags that at least one source failed). Attribution mandatory in
// the brochure: see EXPOSE_GEO_ATTRIBUTION.
export const EXPOSE_POI_CATEGORIES = [
    'oev',
    'bildung',
    'einkaufen',
    'gesundheit',
    'freizeit',
    'gastronomie',
];
export const EXPOSE_POI_CATEGORY_LABELS = {
    oev: 'Öffentlicher Verkehr',
    bildung: 'Bildung',
    einkaufen: 'Einkaufen',
    gesundheit: 'Gesundheit',
    freizeit: 'Freizeit',
    gastronomie: 'Gastronomie',
};
export const exposePoiSchema = z.object({
    name: z.string(),
    distanzM: z.number(),
    gehMin: z.number(),
    veloMin: z.number(),
    autoMin: z.number(),
    /** WGS84 — for the map pins (E4). Older snapshots lack these: pin skipped. */
    lat: z.number().optional(),
    lon: z.number().optional(),
});
export const EXPOSE_SCORE_KEYS = [
    'konnektivitaet',
    'bildung',
    'einkaufen',
    'gesundheit',
    'freizeit',
    'gastronomie',
    'stille',
    'aussicht',
    'immissionen',
];
export const EXPOSE_SCORE_LABELS = {
    konnektivitaet: 'Konnektivität',
    bildung: 'Bildung',
    einkaufen: 'Einkaufen',
    gesundheit: 'Gesundheit',
    freizeit: 'Freizeit',
    gastronomie: 'Gastronomie',
    stille: 'Stille',
    aussicht: 'Aussicht',
    immissionen: 'Immissionen',
};
/**
 * Standort detail pages (E10) — one page per theme, mirroring the example
 * exposé. Each carries its score, its own map and the rows/POIs that justify
 * the score; a page auto-hides when its data is missing.
 */
export const EXPOSE_STANDORT_PAGES = [
    'stille',
    'bildung',
    'einkaufen',
    'aussicht',
    'konnektivitaet',
    'immissionen',
];
/** Which POI categories (if any) a detail page lists under its map. */
export const EXPOSE_STANDORT_PAGE_POI = {
    bildung: ['bildung'],
    einkaufen: ['einkaufen', 'gastronomie'],
    konnektivitaet: ['oev'],
};
export const exposeLocationSnapshotSchema = z
    .object({
    geocode: z
        .object({
        lat: z.number(),
        lon: z.number(),
        /** LV95 coordinates (E/N) — the federal identify APIs speak sr=2056. */
        e: z.number(),
        n: z.number(),
        matchText: z.string().optional(),
    })
        .partial({ matchText: true }),
    hoeheM: z.number().optional(),
    laerm: z
        .object({
        strasseTagDb: z.number().optional(),
        strasseNachtDb: z.number().optional(),
        bahnTagDb: z.number().optional(),
        bahnNachtDb: z.number().optional(),
    })
        .optional(),
    radon: z.object({ probabilityProzent: z.number() }).optional(),
    seismik: z
        .object({ zone: z.string().optional(), baugrundklasse: z.string().optional() })
        .optional(),
    antennen: z
        .object({
        anzahlUmkreis: z.number(),
        naechsteDistanzM: z.number().optional(),
        technologien: z.array(z.string()),
    })
        .optional(),
    luft: z.object({ no2: z.number().optional(), pm10: z.number().optional() }).optional(),
    oevGueteklasse: z.string().optional(),
    solar: z
        .object({
        klasse: z.number().optional(),
        klasseText: z.string().optional(),
        einstrahlungKwhM2: z.number().optional(),
    })
        .optional(),
    /**
     * Horizon openness (E10) from swissALTI3D elevation profiles in four
     * directions: the highest angle any terrain within 2 km subtends above the
     * object. ~0° or negative = free view, high = hemmed in by a slope. It is
     * TERRAIN only — buildings and trees are not in the model — which is why
     * the brochure words it as "Geländehorizont", not "Aussicht garantiert".
     */
    fernsicht: z
        .object({
        /** Mean of the four directional maxima, in degrees. */
        horizontGrad: z.number(),
        /** Most open direction, for the wording ("frei nach Süden"). */
        offensteRichtung: z.string().optional(),
    })
        .optional(),
    // partialRecord: sparse by nature (a source may yield no POIs/score at all);
    // v4's z.record() with an enum key would demand every category be present.
    poi: z.partialRecord(z.enum(EXPOSE_POI_CATEGORIES), z.array(exposePoiSchema)).optional(),
    scores: z.partialRecord(z.enum(EXPOSE_SCORE_KEYS), z.number()).optional(),
    gesamtScore: z.number().optional(),
    /** At least one source failed — the brochure hides the affected rows. */
    partial: z.boolean(),
    fetchedAt: z.string(),
})
    .partial({ geocode: true });
/** Mandatory attribution (disclaimer page + under Standort blocks). */
export const EXPOSE_GEO_ATTRIBUTION = 'Standort-Daten: Bundesamt für Landestopografie swisstopo, BAFU, BAG, BAKOM, BFE, ARE, BAV (geo.admin.ch) · POI-Daten: © OpenStreetMap contributors (ODbL)';
/**
 * Deterministic travel times from beeline distance (research doc §2d): detour
 * factor 1.3, walking 4.8 km/h, cycling 15 km/h, driving 30 km/h in town,
 * rounded up, minimum 1 minute. Same formula the CRM and the PDF print.
 */
export function travelTimes(distanzM) {
    const routeKm = (Math.max(0, distanzM) * 1.3) / 1000;
    const minutes = (speedKmh) => Math.max(1, Math.ceil((routeKm / speedKmh) * 60));
    return { gehMin: minutes(4.8), veloMin: minutes(15), autoMin: minutes(30) };
}
// ───────────────────────────── API DTOs ─────────────────────────────────────
export const exposeOverrideSchema = z.object({
    bodyTextOverride: z.string().max(200_000).nullable(),
});
export const exposeBlocksUpdateSchema = z.object({
    blocks: exposeBlocksSchema,
});
/** E18: hide the sales price brochure-wide ("Auf Anfrage"). */
export const exposePreisAnfrageSchema = z.object({
    preisAufAnfrage: z.boolean(),
});
// ───────────────────────── Mieterspiegel (E19, Rendite) ─────────────────────
/**
 * One rental unit in the Mieterspiegel table. Deliberately WITHOUT any tenant
 * name/contact field — the brochure table is anonymised (example doc: "Per
 * DATUM, anonymisiert"), and a field that cannot exist cannot leak PII.
 */
export const mieterspiegelRowSchema = z.object({
    /** e.g. "Ladenlokal, EG links" / "5-Zimmer-Wohnung, 2. OG". */
    bezeichnung: z.string().trim().min(1).max(80),
    /** Free text, e.g. "06.2026" — printed verbatim; empty for Leerstand. */
    mietbeginn: z.string().trim().max(20).optional(),
    /** Netto-Mietzins pro Monat in CHF (Soll for vacant units). */
    nettomieteMonat: z.number().min(0).max(1_000_000),
    /** Heiz-/Nebenkosten pro Monat in CHF. */
    nkMonat: z.number().min(0).max(1_000_000).optional(),
    status: z.enum(['vermietet', 'leerstand']),
});
/** The whole table as stored on the exposé (edited in the CRM, rendered in
 *  the brochure with COMPUTED totals + Leerstandsquote — never hand-summed). */
export const mieterspiegelSchema = z.object({
    /** "Per …"-Stand shown above the table, e.g. "23.06.2026". */
    stand: z.string().trim().max(20).optional(),
    rows: z.array(mieterspiegelRowSchema).max(60),
});
export const exposeMieterspiegelUpdateSchema = z.object({
    mieterspiegel: mieterspiegelSchema,
});
export const exposeSectionEditSchema = z.object({
    section: exposeTextSectionSchema,
    /** null clears the staff edit (falls back to the generated text on next regenerate). */
    text: z.string().max(20_000).nullable(),
});
// ───────────────────── Photo presentation metadata (E7) ─────────────────────
/**
 * Per-photo presentation flags the EXPOSÉ owns, keyed by `mandate_photos.id`.
 * Deliberately NOT on MandatePhoto: the photo domain belongs to the shooting
 * step, and this is a brochure-presentation decision. Absent id = no override,
 * and the renderer falls back to {@link looksLikeVisualisierung}.
 */
export const exposePhotoMetaEntrySchema = z.object({
    /** Staged/CGI image — labelled "Visualisierung" in the brochure. */
    visualisierung: z.boolean(),
});
export const exposePhotoMetaSchema = z.record(z.string().uuid(), exposePhotoMetaEntrySchema);
export const exposePhotoMetaUpdateSchema = z.object({
    photoId: z.string().uuid(),
    /** null clears the override → back to the filename/caption heuristic. */
    visualisierung: z.boolean().nullable(),
});
// ────────────────────────── Grundriss selection (E8) ────────────────────────
/**
 * Documents whose pages go into the Grundriss block, in brochure order
 * (`mandate_documents.id`). An explicit staff choice on purpose: the document
 * catalogue has no `grundriss` key — floor plans arrive under `bauplaene` or
 * `katasterplan`, next to Baubeschriebe and cubature sheets that must NOT end
 * up in a sales brochure. Capped so a 60-page Baubeschrieb cannot be dropped
 * into the exposé by accident.
 */
export const EXPOSE_GRUNDRISS_MAX_DOCS = 6;
/** Hard cap on rendered plan pages, whatever the selected documents contain. */
export const EXPOSE_GRUNDRISS_MAX_PAGES = 8;
/** Doc keys offered first in the picker — where floor plans actually live. */
export const EXPOSE_GRUNDRISS_DOC_KEYS = ['bauplaene', 'katasterplan'];
export const exposeGrundrissSchema = z.array(z.string().uuid()).max(EXPOSE_GRUNDRISS_MAX_DOCS);
export const exposeGrundrissUpdateSchema = z.object({
    documentIds: exposeGrundrissSchema,
});
// ─────────────────── Gemeinde / Demografie snapshot (E9) ────────────────────
// Municipality reference data (BFS STATPOP), snapshotted onto the exposé at
// generate time exactly like the Standort snapshot: the brochure must keep
// showing the figures it was generated with, and a BFS outage must never
// block a regenerate. Everything optional — each figure degrades on its own.
export const exposeJahresWertSchema = z.object({ jahr: z.number(), wert: z.number() });
export const exposeGemeindeSnapshotSchema = z
    .object({
    bfsNr: z.number(),
    name: z.string().optional(),
    /** 2-letter canton code as BFS publishes it. */
    kanton: z.string().optional(),
    bezirk: z.string().optional(),
    /** Ständige Wohnbevölkerung of the newest published year. */
    einwohner: z.number().optional(),
    jahr: z.number().optional(),
    /** Year-over-year growth of the newest year, in percent. */
    wachstumProzent: z.number().optional(),
    /** Growth per year, oldest first — the example's little trend table. */
    wachstumReihe: z.array(exposeJahresWertSchema).optional(),
    maennerProzent: z.number().optional(),
    frauenProzent: z.number().optional(),
    auslaenderProzent: z.number().optional(),
    auslaenderReihe: z.array(exposeJahresWertSchema).optional(),
    /** Same figures for canton and country — the comparison the example draws. */
    kantonEinwohner: z.number().optional(),
    kantonAuslaenderProzent: z.number().optional(),
    chEinwohner: z.number().optional(),
    chAuslaenderProzent: z.number().optional(),
    /** Communal tax multiplier in % — ONLY where our own layer covers the
     *  canton comparably (ZG/ZH/AG/BL/TG/SG). Absent elsewhere on purpose:
     *  BE/LU publish a bare multiplier and SZ/GE/VD a different base, so a
     *  number there would be a false comparison, not a missing one. */
    steuerfussGemeinde: z.number().optional(),
    /** E14 Steuern & Abgaben — ESTV-Steuerrechner, yearly snapshot. Raw
     *  figures stay in the CANTON'S OWN scale (BE Steueranlage, SG %, …); the
     *  brochure prints them as returned and says so — converting between
     *  canton scales would manufacture false comparability. */
    steuern: z
        .object({
        /** Tax year the ESTV calculator computed with (falls back one year
         *  until the canton has published the new one). */
        jahr: z.number(),
        kantonSatz: z.number().optional(),
        gemeindeSatz: z.number().optional(),
        kircheRef: z.number().optional(),
        kircheRoem: z.number().optional(),
        kircheChrist: z.number().optional(),
        /** Total yearly income tax (Bund+Kanton+Gemeinde, konfessionslos) at
         *  fixed taxable-income household profiles. */
        profile: z
            .array(z.object({
            label: z.string(),
            einkommenChf: z.number(),
            steuerChf: z.number(),
        }))
            .optional(),
    })
        .optional(),
    /** E14 Krankenkassenprämien — BAG open data, derived in-repo per year.
     *  Median of all OKP offers incl. accident cover; adults franchise 300,
     *  child (first child) franchise 0. */
    praemien: z
        .object({
        jahr: z.number(),
        /** Prämienregion key "<KT>-<n>"; n=0 when the canton is one region. */
        region: z.string(),
        erwachsenerChf: z.number(),
        kindChf: z.number(),
        kantonErwachsenerChf: z.number().optional(),
        schweizErwachsenerChf: z.number().optional(),
    })
        .optional(),
    /** At least one source failed — the brochure says so rather than implying
     *  the gaps are real zeroes. */
    partial: z.boolean(),
    fetchedAt: z.string(),
})
    .partial({ partial: true, fetchedAt: true });
/** Mandatory source line under the Gemeinde/Demografie blocks. */
export const EXPOSE_BFS_ATTRIBUTION = 'Bevölkerungsdaten: BFS – STATPOP (ständige Wohnbevölkerung), Stand jeweils 31.12.';
/** Mandatory source line under the Steuern & Abgaben block (E14). */
export const EXPOSE_STEUERN_ATTRIBUTION = 'Steuern: ESTV-Steuerrechner (Steuerfüsse und Beispielrechnungen) · Prämien: BAG-Prämiendaten (opendata.swiss).';
/**
 * Heuristic DEFAULT for the Visualisierung label, applied when staff set no
 * explicit flag. Biased towards labelling: an unlabelled staged render is a
 * transparency problem, a wrongly labelled real photo is merely conservative.
 * Matches our own photo-pipeline output names (`_visualisierung`, `_staged`)
 * plus the common German/English terms.
 */
const VISUALISIERUNG_HINT = /(visualisierung|visualisation|visualization|home[\s_-]*staging|staged|staging|render(ing)?|cgi)/i;
export function looksLikeVisualisierung(...texts) {
    return texts.some((t) => Boolean(t) && VISUALISIERUNG_HINT.test(t));
}
/** Staff override wins; otherwise the filename/caption heuristic decides. */
export function isVisualisierung(meta, photoId, ...texts) {
    const override = meta?.[photoId]?.visualisierung;
    return override ?? looksLikeVisualisierung(...texts);
}
// ─────────────────────────── Reference number (E7) ──────────────────────────
/**
 * Human-quotable object reference, derived deterministically from the mandate
 * UUID — we have no sequential listing number and inventing a counter would
 * need a column in the shared Mandate table (master-session territory).
 * 32 bits of the UUID in base36: stable across regenerates, unique enough for
 * a display/quoting reference (never used as a key).
 */
export function exposeReferenzNr(mandateId) {
    const hex = mandateId.replace(/-/g, '').slice(0, 8);
    if (!/^[0-9a-f]{8}$/i.test(hex))
        return '';
    return `MLG-${parseInt(hex, 16).toString(36).toUpperCase().padStart(6, '0')}`;
}
export const KAUFNEBENKOSTEN_RATES = {
    // Deutschschweiz — cantons WITHOUT a Handänderungssteuer (land-registry only).
    ZH: { handaenderungPct: null, notariatGrundbuchPct: 0.3 },
    ZG: { handaenderungPct: null, notariatGrundbuchPct: 0.3 },
    SZ: { handaenderungPct: null, notariatGrundbuchPct: 0.3 },
    UR: { handaenderungPct: null, notariatGrundbuchPct: 0.3 },
    GL: { handaenderungPct: null, notariatGrundbuchPct: 0.3 },
    SH: { handaenderungPct: null, notariatGrundbuchPct: 0.3 },
    AG: { handaenderungPct: null, notariatGrundbuchPct: 0.4 },
    // Cantons WITH a Handänderungssteuer (buyer share of the statutory rate).
    BE: { handaenderungPct: 1.8, notariatGrundbuchPct: 0.5 },
    LU: { handaenderungPct: 1.5, notariatGrundbuchPct: 0.4 },
    OW: { handaenderungPct: 1.0, notariatGrundbuchPct: 0.3 },
    NW: { handaenderungPct: 1.0, notariatGrundbuchPct: 0.3 },
    FR: { handaenderungPct: 1.5, notariatGrundbuchPct: 0.5 },
    SO: { handaenderungPct: 1.1, notariatGrundbuchPct: 0.5 },
    BS: { handaenderungPct: 1.5, notariatGrundbuchPct: 0.3 },
    BL: { handaenderungPct: 1.25, notariatGrundbuchPct: 0.4 },
    AR: { handaenderungPct: 1.0, notariatGrundbuchPct: 0.3 },
    AI: { handaenderungPct: 1.0, notariatGrundbuchPct: 0.3 },
    SG: { handaenderungPct: 1.0, notariatGrundbuchPct: 0.4 },
    GR: { handaenderungPct: 1.0, notariatGrundbuchPct: 0.5 },
    TG: { handaenderungPct: 1.0, notariatGrundbuchPct: 0.4 },
    // Romandie / Ticino — higher, value-based, buyer share.
    TI: { handaenderungPct: 1.1, notariatGrundbuchPct: 0.6 },
    VD: { handaenderungPct: 1.65, notariatGrundbuchPct: 1.0 },
    VS: { handaenderungPct: 1.5, notariatGrundbuchPct: 0.5 },
    NE: { handaenderungPct: 1.65, notariatGrundbuchPct: 0.7 },
    GE: { handaenderungPct: 1.5, notariatGrundbuchPct: 1.0 },
    JU: { handaenderungPct: 1.05, notariatGrundbuchPct: 0.6 },
};
/**
 * De 26 officiële Kantons-Kürzel in hoofdletters — **één lijst in shared**
 * (Vermarktungs-Feed-Contract §7). Afgeleid uit de sleutels van
 * {@link KAUFNEBENKOSTEN_RATES}, nooit een tweede literal: de tabel hierboven
 * is al de enige kantonlijst van dit pakket, en een tweede opsomming zou
 * precies de drift maken die het contract verbiedt.
 *
 * Constructie (contract §7, verbatim): de expliciete annotatie
 * `: Record<string, KaufnebenkostenRate>` is vóór de literal weggehaald en de
 * literal draagt `as const satisfies …` — mét de annotatie zou
 * `keyof typeof KAUFNEBENKOSTEN_RATES` gewoon `string` blijven en zou
 * {@link KantonKuerzel} stil tot `string` degraderen. `Object.keys` geeft in
 * TypeScript altijd `string[]`; de cast maakt er de 26-literal-unie van. Het
 * `satisfies` houdt de vorm-controle die de annotatie deed.
 *
 * Runtime-gepind (`expose.test.ts`): lengte 26, alle `^[A-Z]{2}$`, en
 * {@link computeKaufnebenkosten} kent elke waarde. Een runtime-pin en geen
 * type-pin, omdat `packages/shared/tsconfig.json` testbestanden uitsluit.
 *
 * `Object.freeze` om dezelfde reden als bij de andere registers van dit pakket
 * (`VERBOTENE_FELDER`, `PROJEKTION_BESTEMMING`): `Object.keys` levert een VERSE,
 * muteerbare array, en een `z.enum(...)` die daaruit is opgebouwd zou na een
 * `push` elders in het proces een andere kantonlijst beloven dan hij bewaakt.
 */
export const KANTON_KUERZEL = Object.freeze(Object.keys(KAUFNEBENKOSTEN_RATES));
/**
 * Type-guard op {@link KANTON_KUERZEL}. Bewust `Array.includes` en niet
 * `x in KAUFNEBENKOSTEN_RATES`: `in` loopt de prototypeketen af, dus
 * `'toString'` zou daar `true` geven. (Gedrag ongewijzigd t.o.v. de oude
 * `code in …`-poort: die zag alleen HOOFDletters en `'TOSTRING'` staat niet
 * op de keten.)
 */
export function isKantonKuerzel(x) {
    return KANTON_KUERZEL.includes(x);
}
/**
 * Compute the Kaufnebenkosten breakdown for a price + canton, or null when the
 * canton is unknown — the renderer then keeps the honest range prose rather
 * than printing an invented figure.
 */
export function computeKaufnebenkosten(preis, kanton) {
    const code = kanton?.toUpperCase();
    if (!code || !isKantonKuerzel(code) || !(preis > 0))
        return null;
    // `code` is na de guard een literal-Kürzel, dus de index is niet
    // `noUncheckedIndexedAccess`-verdacht en de oude `!` is overbodig geworden.
    const rate = KAUFNEBENKOSTEN_RATES[code];
    const lines = [];
    if (rate.handaenderungPct !== null) {
        lines.push({
            label: `Handänderungssteuer (${pctDe(rate.handaenderungPct)})`,
            chf: Math.round((preis * rate.handaenderungPct) / 100),
        });
    }
    else {
        lines.push({ label: 'Handänderungssteuer', chf: 0 });
    }
    lines.push({
        label: `Notariat & Grundbuch (ca. ${pctDe(rate.notariatGrundbuchPct)})`,
        chf: Math.round((preis * rate.notariatGrundbuchPct) / 100),
    });
    const total = lines.reduce((a, l) => a + l.chf, 0);
    return { lines, total, kanton: code };
}
/** de-CH percent for the labels above ("1.8" → "1,8 %"). */
function pctDe(v) {
    const r = Math.round(v * 100) / 100;
    return `${(Number.isInteger(r) ? String(r) : r.toString()).replace('.', ',')} %`;
}
// ───────────────────────────── Derived bodyText ─────────────────────────────
const fmtChf = (n) => Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, "'");
/**
 * Flat-text render of the structured exposé — the backward-compatible artifact
 * the publication bridge (F9) consumes as `bodyText` until E7 structures the
 * publish payload. Pure + deterministic: same inputs → same text.
 */
export function buildExposeBodyText(eckdaten, sections) {
    const lines = [];
    const title = eckdaten.titel || 'Verkaufsobjekt';
    lines.push(`# ${title}`);
    const ort = eckdaten.gemeinde || eckdaten.adresseText;
    if (ort)
        lines.push(`_${ort}_`);
    if (sections.teaser)
        lines.push(sections.teaser);
    const facts = [
        ['Objekttyp', eckdaten.objektTypLabel],
        ['Zimmer', eckdaten.zimmer?.toString()],
        ['Nettowohnfläche', eckdaten.flaechen?.nettowohnflaeche?.toString().concat(' m²')],
        ['Grundstücksfläche', eckdaten.flaechen?.grundstuecksflaeche?.toString().concat(' m²')],
        ['Baujahr', eckdaten.baujahr?.toString()],
        ['Zustand', eckdaten.zustandLabel],
        [
            'Verkaufspreis',
            eckdaten.preis?.verkaufspreis !== undefined
                ? `CHF ${fmtChf(eckdaten.preis.verkaufspreis)}`
                : undefined,
        ],
    ];
    const factLines = facts
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `- **${k}:** ${v}`);
    if (factLines.length)
        lines.push('## Eckdaten', factLines.join('\n'));
    if (sections.beschreibung)
        lines.push('## Beschreibung', sections.beschreibung);
    if (sections.highlightsText)
        lines.push('## Highlights', sections.highlightsText);
    if (sections.investitionen?.length) {
        lines.push('## Investitionen & Highlights', sections.investitionen.map((i) => `- ${i}`).join('\n'));
    }
    if (sections.ausstattungText)
        lines.push('## Ausstattung', sections.ausstattungText);
    else if (eckdaten.ausstattungLabels?.length) {
        lines.push('## Ausstattung', eckdaten.ausstattungLabels.join(' · '));
    }
    const energieBits = [];
    if (eckdaten.energie?.waermeerzeugungLabel) {
        energieBits.push(`- **Wärmeerzeugung:** ${eckdaten.energie.waermeerzeugungLabel}`);
    }
    if (eckdaten.energie?.energiestandardLabel) {
        energieBits.push(`- **Energiestandard:** ${eckdaten.energie.energiestandardLabel}`);
    }
    if (eckdaten.energie?.geak?.klasse) {
        energieBits.push(`- **GEAK-Klasse:** ${eckdaten.energie.geak.klasse}`);
    }
    if (energieBits.length)
        lines.push('## Energie', energieBits.join('\n'));
    lines.push('## Lage & Umgebung', sections.lage || 'Detailangaben zur Lage und Umgebung werden ergänzt.');
    return lines.join('\n\n');
}
